/**
 * Sharing a chat with people from its project (07 Workshop, Share a chat).
 * Before it is shared it is "Share this chat": the project's people each with
 * Add, which stages them (Remove takes them back out), and Share sends them all
 * at once. Once shared it is "People in this chat": who is in it, with Remove,
 * and the rest of the project with Add, each at once. A chat can only be
 * shared with the project's people, so when nobody else is in the project yet
 * the dialog only warns: no people and no Add, since adding someone here would
 * share the project, not the chat (user call, 12 Oct). Everyone in a chat sees
 * its whole history and talks to the same assistant.
 */
import { useMemo, useState, type ReactNode } from "react";
import { Info, TriangleAlert } from "lucide-react";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { UserMark } from "@/app/routes/dashboard/components/UserMark";
import { useAuthStore } from "@/stores/authStore";
import { usePermissions } from "@/hooks/usePermissions";
import type { WorkshopPerson, WorkshopSession } from "@/types/workshop.type";
import { useWorkshopMembers } from "../api/useWorkshopMembers";
import { useWorkshopSessions } from "../api/useWorkshopSessions";
import WorkshopDialog from "./WorkshopDialog";

type Props = {
  projectId: string;
  session: WorkshopSession;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present when the viewer owns the project: the warning says it is theirs to share. */
  onManageProject?: () => void;
};

const nameOf = (person: WorkshopPerson) => person.name?.trim() || "A teammate";

function Row({ person, detail, action }: { person: WorkshopPerson; detail: string; action?: ReactNode }) {
  return (
    <div className="ws-person">
      <UserMark name={nameOf(person)} size={32} />
      <div className="min-w-0 flex-1">
        <div className="ws-person-name">{nameOf(person)}</div>
        <div className="ws-person-detail">{detail}</div>
      </div>
      {action}
    </div>
  );
}

function Head({ label, count }: { label: string; count: number }) {
  return (
    <h3 className="ws-share-head">
      {label}
      <span className="ws-share-count">{count}</span>
    </h3>
  );
}

export default function ShareThreadModal({
  projectId,
  session,
  isOpen,
  onOpenChange,
  onManageProject,
}: Props) {
  const { owner, members, isLoading } = useWorkshopMembers(isOpen ? projectId : null);
  const { shareThread, removeParticipant } = useWorkshopSessions(projectId);
  const { isGuest } = usePermissions();
  const viewerId = useAuthStore((s) => s.user?.id ?? null);
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const participants = useMemo(() => session.participants ?? [], [session.participants]);
  const alreadyShared = !!session.sharedAt;
  const title = session.title?.trim() || "This chat";

  const projectPeople = useMemo<WorkshopPerson[]>(
    () => [
      ...(owner ? [owner] : []),
      ...members.map((m) => ({ userId: m.userId, name: m.name ?? m.email })),
    ],
    [owner, members],
  );
  const candidates = useMemo(() => {
    const taken = new Set([session.ownerUserId, ...participants.map((p) => p.userId)]);
    return projectPeople.filter((p) => !taken.has(p.userId));
  }, [projectPeople, participants, session.ownerUserId]);
  const starter: WorkshopPerson | null =
    session.owner ?? projectPeople.find((p) => p.userId === session.ownerUserId) ?? null;

  // Nobody but the chat's owner is in the project: there is no one to pick.
  const nobodyElse = !isLoading && projectPeople.every((p) => p.userId === session.ownerUserId);
  const ownsProject = !!onManageProject && !isGuest;

  const close = () => {
    setPicked(new Set());
    onOpenChange(false);
  };

  const share = () => {
    const people = candidates.filter((p) => picked.has(p.userId));
    if (people.length === 0) return;
    // Shown on the chat at once; the hook rolls back if it is refused.
    shareThread.mutate({ sessionId: session.id, people });
    close();
  };

  const toggle = (userId: string) =>
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  const onlyProjectPeople = (
    <p className="ws-share-hint">
      <Info aria-hidden="true" />
      To add someone else, add them to the project first.
    </p>
  );

  const remove = (p: WorkshopPerson) => (
    <KitButton
      type="button"
      variant="ghost"
      size="sm"
      aria-label={`Remove ${nameOf(p)} from the chat`}
      onClick={() => removeParticipant.mutate({ sessionId: session.id, userId: p.userId, leaving: false })}
    >
      Remove
    </KitButton>
  );

  const body = isLoading ? (
    <div className="flex flex-col gap-2 py-1.5" aria-hidden>
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center gap-3 px-2">
          <span className="size-8 shrink-0 animate-pulse rounded-full bg-co-hover" />
          <span className="h-3 w-32 animate-pulse rounded-full bg-co-hover" />
        </div>
      ))}
    </div>
  ) : nobodyElse ? (
    <p className="ws-share-hint ws-share-hint--warn" role="note">
      <TriangleAlert aria-hidden="true" />
      {ownsProject
        ? "Nobody else is in this project yet, and a chat can only be shared with the project's people. Share the project with them first, then come back to this chat."
        : isGuest
          ? "Nobody else is in this project yet, and a chat can only be shared with the project's people. Ask your contact on the team to add them to the project first."
          : "Nobody else is in this project yet, and a chat can only be shared with the project's people. Ask the project's owner to add them first."}
    </p>
  ) : alreadyShared ? (
    <>
      <section className="ws-share-group" aria-label="In this chat">
        <Head label="In this chat" count={(starter ? 1 : 0) + participants.length} />
        {starter ? (
          <Row
            person={starter}
            detail={starter.userId === viewerId ? "You · started this chat" : "Started this chat"}
          />
        ) : null}
        {participants.map((p) => (
          <Row key={p.userId} person={p} detail={p.userId === viewerId ? "You" : "In this chat"} action={remove(p)} />
        ))}
      </section>
      {candidates.length > 0 ? (
        <section className="ws-share-group" aria-label="Add from this project">
          <Head label="Add from this project" count={candidates.length} />
          {candidates.map((p) => (
            <Row
              key={p.userId}
              person={p}
              detail="In the project"
              action={
                <KitButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  aria-label={`Add ${nameOf(p)} to the chat`}
                  onClick={() => shareThread.mutate({ sessionId: session.id, people: [p] })}
                >
                  Add
                </KitButton>
              }
            />
          ))}
        </section>
      ) : null}
      {onlyProjectPeople}
    </>
  ) : (
    <>
      <section className="ws-share-group" aria-label="In this project">
        <Head label="In this project" count={candidates.length} />
        {candidates.map((p) => {
          const added = picked.has(p.userId);
          return (
            <Row
              key={p.userId}
              person={p}
              detail={added ? "Added to the chat just now" : "In the project"}
              action={
                <KitButton
                  type="button"
                  variant={added ? "ghost" : "secondary"}
                  size="sm"
                  aria-label={`${added ? "Remove" : "Add"} ${nameOf(p)}`}
                  onClick={() => toggle(p.userId)}
                >
                  {added ? "Remove" : "Add"}
                </KitButton>
              }
            />
          );
        })}
      </section>
      {onlyProjectPeople}
    </>
  );

  return (
    <WorkshopDialog
      isOpen={isOpen}
      onOpenChange={(next) => (next ? onOpenChange(true) : close())}
      title={alreadyShared ? "People in this chat" : "Share this chat"}
      description={
        alreadyShared
          ? "Everyone here talks to the same assistant and sees the whole chat."
          : `“${title}” is yours alone. People you add see everything in it and talk to Creo here with you.`
      }
      className="ws-share ws-share-thread"
      {...(alreadyShared || nobodyElse || isLoading
        ? {
            actions: (
              <KitButton type="button" className="ws-dialog-btn" onClick={close}>
                Done
              </KitButton>
            ),
          }
        : {
            cancelLabel: "Cancel",
            confirmLabel: "Share",
            confirmDisabled: picked.size === 0,
            onConfirm: share,
          })}
    >
      {body}
    </WorkshopDialog>
  );
}
